import styled, { css } from "styled-components";
import { colors, header, transition, boxTrayToggleMobileHeight } from "../../../styles/variables";
import breakpoints from "../../../styles/breakpoints";
import { standalone } from "../../../styles/displayMode";

interface styleProps {
  isOpened?: boolean;
  smUpTop?: string;
  smUpHeightOffset?: string;
  mdUpTop?: string;
  mdUpHeightOffset?: string;
}

export const SmDownBackdrop = styled.div<{ isOpened: boolean }>`
  ${breakpoints.smDown} {
    background: ${(props) => (props.isOpened ? "rgba(0, 0, 0, 0.5)" : "rgba(0, 0, 0, 0)")};
    ${(props) => !props.isOpened && "visibility: hidden;"}
    height: 100vh;
    width: 100vw;
    position: fixed;
    transition: ${transition.headerMenuBarTransition};
    pointer-events: ${(props) => (props.isOpened ? "auto" : "none")};
  }
  ${breakpoints.smUp} {
    display: none;
  }
`;

export const StyledBoxTray = styled.div`
  transition: ${transition.headerMenuBarTransition};
  position: fixed;
  right: 0;
  width: 100%;
  top: 0;
  z-index: ${(props: styleProps) => (props.isOpened ? "950" : "900")};
  ${breakpoints.smUp} {
    top: ${(props) => props.smUpTop || `${header.mobileHeight}`};
    width: 420px;
    transform: translateX(${(props) => (!props.isOpened ? "420px" : "0px")});
    height: calc(100% - ${(props) => `${props.smUpHeightOffset}`});
  }
  ${breakpoints.mdUp} {
    z-index: 6000;
    top: ${(props: styleProps) => props.mdUpTop || `${header.desktopHeight}`};
    height: calc(100% - ${(props) => `${props.mdUpHeightOffset}`});
  }
  ${breakpoints.smDown} {
    position: relative;
    /* Reserve the strip's own toggle height in flow. The inset above
     * the strip is already accounted for by Layout's .content padding-
     * top: env(safe-area-inset-top); adding env here too would double-
     * count and leave a white band below the strip. */
    margin-bottom: ${boxTrayToggleMobileHeight};
  }
`;

export const HolderInner = styled.div<{
  isOpened: boolean;
  isHeaderShown: boolean;
  pageHasOwnHeader?: boolean;
}>`
  ${breakpoints.smUp} {
    height: 100%;
    box-shadow: 0 5px 10px 0 rgba(0, 0, 0, 0.1);
  }
  ${breakpoints.smDown} {
    display: ${(props) => props.isOpened && "block"};
    transition: ${transition.headerMenuBarTransition};
    ${(props) =>
      css`
        transform: translateY(0);
        position: fixed;
        overflow: hidden;
        margin-top: -${boxTrayToggleMobileHeight};
        box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.15);
        height: ${props.isOpened ? `calc(100vh - ${header.mobileHeight})` : `${boxTrayToggleMobileHeight}`};
      `}
    left: 0;
    /* margin-top pulls this up by the strip's own height, so top:
     * header.mobileHeight renders it flush with the viewport top. Pages with
     * their own header (hideHeader) get that flush position — the standard
     * header isn't there to sit below, and the page's chrome starts after the
     * height StyledBoxTray already reserves in flow. */
    ${(props) =>
      css`
        top: ${props.isHeaderShown && !props.pageHasOwnHeader
          ? header.withTabBarMobileHeightBoxToggle
          : header.mobileHeight};
      `}
    z-index: 2500;
    width: 100%;

    /* Standalone mode (Capacitor / installed PWA): the web header
     * doesn't render, so don't shift the toggle 68px up to align with
     * it. Anchor at top:0, give HolderInner the coral bg (so the
     * padding-top notch backdrop is coral, not transparent — the toggle
     * pill child has its own coral but only fills the area below the
     * padding), and pad by the safe-area inset so the pills sit below
     * the notch.
     *
     * The closed-state height adds env(safe-area-inset-top) to
     * boxTrayToggleMobileHeight (rather than reusing the bare value):
     * the global box-sizing: border-box rule means the height value is
     * the BORDER-BOX height, so the padding-top eats from inside it and
     * would crush the pill content area to (68 - safe-area). Bumping
     * the height by the same inset preserves the original 68px content
     * area and matches what the web build renders.
     *
     * When the tray is open, the height fills the screen minus the
     * gesture-nav inset at the bottom so the close-tray pill sits above
     * the Android gesture bar — without this, taps in the bottom strip
     * are intercepted by the OS gesture instead of the WebView. The
     * top inset is absorbed by padding-top, so the height value itself
     * doesn't need to subtract it. */
    ${(props) =>
      standalone(css`
        top: 0;
        margin-top: 0;
        background-color: ${colors.coral};
        padding-top: env(safe-area-inset-top);
        height: ${props.isOpened
          ? "calc(100vh - env(safe-area-inset-bottom))"
          : `calc(${boxTrayToggleMobileHeight} + env(safe-area-inset-top))`};
      `)}
  }
`;
